import {Flowchart as FlowchartHelper} from "@/core/helpers/flowchart";
import {Survey} from "@/core/interfaces/survey/Survey";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import {ProductRecommendation} from "@/core/interfaces/Product";
import {FileManagerFile} from "@/core/interfaces/FileManagerFile";
import {NotificationSequence} from "@/notification-sequences/interfaces/NotificationSequence";

export interface Flowchart {
    /** Unique identifier for the flowchart */
    id: number;
    /** Display name of the flowchart */
    name: string;
    /** Detailed description of the flowchart purpose */
    description: string;
    /** URL slug for the flowchart */
    url?: string;
    /** Optional laboratory id */
    laboratory_id?: number;
    /** Collection of nodes belonging to this flowchart */
    nodes: FlowchartNode[];
    /** Collection of edges connecting the nodes */
    edges: FlowchartEdge[];
    /** The survey associated with this flowchart */
    survey: Survey;
    /** Timestamp when the flowchart was created */
    created_at: Date;
    /** Timestamp when the flowchart was last updated */
    updated_at: Date;
    /** Product recommendations */
    recommendations: ProductRecommendation[];
}

/**
 * Payload to update the position of a single flowchart node.
 */
export interface FlowchartNodePositionUpdate {
    /** The node ID. */
    id: number;
    /** The new position coordinates of the node. */
    position: { x: number; y: number };
}

/** A document reference carried by an output node action payload. */
export interface FlowchartDocument {
    /** Display label of the document (print actions). */
    label: string;
    /** Short class name of the document, resolved server-side. */
    class: string;
    /** Whether printing is disabled until a product recommendation is picked. */
    requires_recommendations?: boolean;
    /** Whether the document renders the pharmacist free comment. */
    accepts_comment?: boolean;
}

/**
 * MSSanté prefill carried by a terminal node: subject and body templates
 * whose {{placeholders}} are replaced at runtime, and the documents to
 * generate as attachments.
 */
export interface FlowchartMssantePayload {
    subject: string;
    body: string;
    documents: FlowchartDocument[];
}

/**
 * Fields shared by every node, whatever its type.
 */
export interface BaseFlowchartNode {
    /** Unique identifier for the node */
    id: number;
    /** ID of the flowchart this node belongs to */
    flowchart_id: number;
    /** Display text for the node */
    label: string;
    /** Description of the node */
    description: string;
    /** Optional hint for the node */
    hint?: string;
    /** JSON definition of handles */
    handles: string;
    /** JSON Position coordinates of the node in the flowchart { x: number; y: number } */
    position: string;
    /** The file associated with the node */
    file?: FileManagerFile;
    /** The sequences linked to the node */
    notification_sequences?: NotificationSequence[]
}

/**
 * Input node, carries no action.
 */
export interface InputFlowchartNode extends BaseFlowchartNode {
    type: 'input';
    action_type: null;
    action_payload: null;
}

/**
 * Intermediate node (branching / question). Carries no action.
 * Its `type` is null on the API side.
 */
export interface ProcessFlowchartNode extends BaseFlowchartNode {
    type: null;
    action_type: null;
    action_payload: null;
}

/**
 * Terminal node that prints one or more documents (prescription, report…).
 */
export interface PrintOutputFlowchartNode extends BaseFlowchartNode {
    type: 'output';
    action_type: 'print';
    action_payload: {
        documents: FlowchartDocument[];
        /** Advice shown to the pharmacist on the node (Markdown). */
        hook?: string;
        mssante?: FlowchartMssantePayload;
    };
}

/**
 * Terminal node that triggers a contact action.
 */
export interface ContactOutputFlowchartNode extends BaseFlowchartNode {
    type: 'output';
    action_type: 'contact';
    action_payload: null;
}

/**
 * Survey node: embeds a survey by slug. Once filled, its verdict slug selects the
 * outgoing edge to follow.
 */
export interface SurveyFlowchartNode extends BaseFlowchartNode {
    type: string;
    action_type: 'survey';
    action_payload: {
        /** Slug of the survey embedded in the node. */
        survey_slug: string;
        /** Advice shown to the pharmacist on the node (Markdown). */
        hook?: string;
    };
    /** Resolved verdict of a `survey` node, displayed on the node once filled. */
    surveyVerdict?: SurveyNodeVerdict;
}

/** Any terminal (output) node. */
export type OutputFlowchartNode =
    | PrintOutputFlowchartNode
    | ContactOutputFlowchartNode;

/**
 * A flowchart node, discriminated by its `action_type`
 * ('print' | 'contact' | 'survey' | null).
 */
export type FlowchartNode =
    | InputFlowchartNode
    | ProcessFlowchartNode
    | OutputFlowchartNode
    | SurveyFlowchartNode;

/**
 * Action type carried by an actionable node.
 */
export type FlowchartActionType = NonNullable<FlowchartNode['action_type']>;

/**
 * Option field types for edge options
 */
export type OptionFieldType = 'single_choice' | 'text' | 'multiple_choice';

/**
 * Option field value
 */
export interface OptionFieldValue {
    /** Unique identifier for this value */
    slug: string;
    /** Display label for this value */
    label: string;
}

/**
 * Option field definition
 */
export interface OptionField {
    /** Type of the field (single_choice, text, multiple_choice) */
    type: OptionFieldType;
    /** Display label for the field */
    label: string;
    /** Available values for choice fields */
    values?: OptionFieldValue[];
    /** Indicates if the field is required */
    required?: boolean;
    /** Priority of the field in the options */
    priority?: number;
}

/**
 * Edge options
 */
export type EdgeOptions = {
    /** Legacy format: simple key-value pairs */
    [key: string]: OptionField;
}

export interface FlowchartEdge {
    /** Unique identifier for the edge */
    id: number;
    /** ID of the flowchart this edge belongs to */
    flowchart_id: number;
    /** ID of the source node */
    from_node_id: number;
    /** ID of the source handle */
    from_handle_id: number;
    /** ID of the target node */
    to_node_id: number;
    /** ID of the target handle */
    to_handle_id: number;
    /** Label text displayed on the edge */
    label: string;
    /** The slug identifier for the edge */
    slug: string;
    /** JSON string of options for the node, if any */
    options?: EdgeOptions;
}

export interface FlowchartInstance {
    /** Unique identifier for the flowchart instance */
    id: number;
    /** The flowchart this instance is for */
    flowchart: Flowchart;
    /** The trod structured verdict */
    verdict: { label: string, documents: { label: string, class: string }[] };
    /** The consumer_key this instance is for */
    consumer_key: number;
    /** The path */
    path: FlowchartInstancePath;
    /** Timestamp when the instance was created */
    created_at: string;
}

/** UI-only fields layered on top of a node for rendering. */
export interface FlowchartNodeUiState {
    /** If true, the node is highlighted */
    highlighted?: boolean;
    /** If true, the node is secondary highlighted */
    secondaryHighlighted?: boolean;
}

/** A node augmented with its rendering state, as passed to the CustomNode component. */
export type CustomNodeProps = FlowchartNode & FlowchartNodeUiState;

/**
 * Verdict resolved from a `survey` node's questionnaire.
 */
export interface SurveyNodeVerdict {
    /** The verdict slug — matches the slug of the outgoing edge to follow. */
    slug: string;
    /** The verdict title. */
    title: string;
    /** The numeric score. */
    score: number;
    /** The answered survey instance id, read by documents embedding the result. */
    instance_id?: number;
}

/**
 * Outcome emitted by a survey action once its questionnaire is answered.
 */
export interface SurveyResolvedAction {
    type: 'survey';
    node: SurveyFlowchartNode;
    /** The resolved verdict, or null when the survey yields none. */
    verdict: SurveyNodeVerdict | null;
}

/**
 * Discriminated payload emitted by an action component when it resolves.
 * Extend this union as new action types gain an outcome to report.
 */
export type ResolvedAction = SurveyResolvedAction;

export interface ActionComponentProps<TNode extends FlowchartNode = FlowchartNode> {
    /** The flowchart helper instance */
    flowchart: FlowchartHelper;
    /** The current node */
    node: TNode;
    /** The patient the flowchart is run for: the insured customer or one of their beneficiaries. */
    consumer: Customer | Beneficiary;
    /** The survey verdicts resolved during the flowchart, keyed by survey node id. */
    surveyVerdicts?: Record<number, SurveyNodeVerdict>;
}

/**
 * Option keys of the last path item holding what the pharmacist added on the terminal
 * node. The `_` prefix is reserved: no edge option uses it.
 */
export const FLOWCHART_INSTANCE_OPTIONS = {
    recommendations: '_recommendations',
    comment: '_comment',
} as const;

export interface EdgePathItem {
    /** The edge slug */
    slug: string;
    /** The edge selected options */
    options: { [key: string]: string | string[] };
}

/**
 * A path item as saved in a flowchart instance: the last one also carries the
 * reserved FLOWCHART_INSTANCE_OPTIONS of the terminal node.
 */
export interface FlowchartInstancePathItem {
    /** The edge slug */
    slug: string;
    /** The edge selected options, plus the reserved options on the last item */
    options: { [key: string]: string | string[] | number[] };
}

export type FlowchartInstancePath = FlowchartInstancePathItem[]

/**
 * Props for the selected edges path.
 */
export type EdgePath = EdgePathItem[]
